/**
 * The chef date range for a surface, held across an orders <-> shopping-list
 * flip and mirrored into the URL.
 *
 * Deliberately thin: every rule lives in the pure helpers in
 * `lib/chefs/date-range-url` so it can be unit-tested (this client has no
 * @testing-library/react and no `renderHook`).
 *
 * Two invariants worth keeping if you edit this:
 *
 *  - **No `useEffect`.** `setSearchParams`'s identity churns with
 *    `location.search`, so an effect that navigates re-runs after its own
 *    write. The range is RESOLVED during render and WRITTEN only from handlers.
 *  - **One write per gesture.** `useSearchParams` closes over the render-time
 *    `searchParams`, so two `setSearchParams` calls in the same tick means the
 *    second one's `prev` is stale and the first one's param is silently
 *    dropped. This is why `DateRangeStepper` reports a whole `DateRange`
 *    through one callback rather than anchor and granularity separately.
 */

import { useCallback, useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router'

import {
  deserializeDateRange,
  PARAM_DATE,
  PARAM_END,
  PARAM_VIEW,
  parseDateRangeParams,
  resolveDateRange,
  serializeDateRange,
  writeDateRangeParams,
} from '@/lib/chefs/date-range-url'
import type { DateGranularity, DateRange } from '@/lib/chefs/service-days'
import { storedRange, useChefDateRangeStore } from '@/stores/chefs/date-range'

export interface ChefDateRange {
  range: DateRange
  /** Stable for the life of the mount — the surface's "now" for label + default math. */
  today: Date
  setRange: (next: DateRange) => void
}

/**
 * @param defaultGranularity the surface's cold-start preference (`'daily'` for
 * the kitchen orders page, `'weekly'` for the shopping list). Applies only
 * while the chef hasn't changed the range this session — a granularity string
 * rather than a `DateRange` so callers carry no memo-stability obligation.
 */
export function useChefDateRange(defaultGranularity: DateGranularity): ChefDateRange {
  const today = useMemo(() => new Date(), [])
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname } = useLocation()

  // Subscribed one primitive at a time: a selector returning a fresh object
  // loops under zustand 5's snapshot-identity check.
  const anchorKey = useChefDateRangeStore((s) => s.anchorKey)
  const granularity = useChefDateRangeStore((s) => s.granularity)
  const endKey = useChefDateRangeStore((s) => s.endKey)
  const seededOn = useChefDateRangeStore((s) => s.seededOn)
  const setStoreRange = useChefDateRangeStore((s) => s.setRange)

  const dateParam = searchParams.get(PARAM_DATE)
  const viewParam = searchParams.get(PARAM_VIEW)
  const endParam = searchParams.get(PARAM_END)

  const resolved = useMemo(
    () =>
      resolveDateRange({
        fromUrl: parseDateRangeParams(dateParam, viewParam, endParam),
        stored: storedRange({ anchorKey, granularity, endKey, seededOn }),
        defaultGranularity,
        today,
      }),
    [dateParam, viewParam, endParam, anchorKey, granularity, endKey, seededOn, defaultGranularity, today],
  )

  // Keyed on `resolved`, which is itself memoized off the serialized primitives,
  // so the Date identities are stable as long as the chosen days are — `range`
  // fans out to `buildServiceDays` and the campus-by-day rollups on every render.
  const range = useMemo<DateRange>(() => deserializeDateRange(resolved), [resolved])

  const setRange = useCallback(
    (next: DateRange) => {
      // Stamped with the SAME mount-time `today` the page uses for its labels,
      // so a change always survives the `seededOn` check for this mount. A tab
      // that crosses midnight and then navigates remounts with the real today,
      // the stale stamp loses, and the chef lands on the current day — which is
      // the behavior the guard exists for.
      setStoreRange(next, today)
      // `/` renders these pages in place for chefs whose first nav item is that
      // slug; don't accrue query params on the landing route.
      if (pathname === '/') {
        return
      }
      setSearchParams((prev) => writeDateRangeParams(prev, serializeDateRange(next)), { replace: true })
    },
    [setStoreRange, today, pathname, setSearchParams],
  )

  return { range, today, setRange }
}
